import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MousePointerClickIcon, PlayIcon, XIcon } from 'lucide-react';
import { ScaledViewport } from '../ScaledViewport';
import { SnakStill } from '../SnakStill';
import { LIVE_SNAK_URL } from './PhoneSnak';
interface WebsiteSnakDemoProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
const WEBSITE_SCREENSHOT = 'https://cdn.magicpatterns.com/uploads/5QCzqcMsRSFXbK8DdQEmgk/Screenshot_2026-10-22_at_12.36.46.png';

/** Matches the REKOVELLE site's own purple so the invitation feels native. */
const SITE_PURPLE = '#5E4291';

/**
 * The existing REKOVELLE HCP website with a compact Snak invitation.
 * Clicking the invitation embeds the live Snak in a window above the page.
 * Positions are percentages of the screenshot.
 */
export function WebsiteSnakDemo({
  open,
  onOpenChange
}: WebsiteSnakDemoProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);
  return <div className="overflow-hidden rounded-[18px] bg-white shadow-[0_40px_80px_-44px_rgba(14,27,51,0.55)] ring-1 ring-[var(--fg)]/10" data-id="element-716" data-mp="1zdghug4yj64w">
      {/* browser chrome */}
      <div className="flex items-center gap-3 border-b border-[var(--fg)]/[0.08] px-4 py-2.5" data-id="element-717" data-mp="1ugq80ajqx6rx">
        <div className="flex gap-1.5" aria-hidden="true" data-id="element-718" data-mp="299rzzmr8aq79">
          <span className="h-2.5 w-2.5 rounded-full bg-[var(--fg)]/15" data-id="element-719" data-mp="24bqhp41mmuwu" />
          <span className="h-2.5 w-2.5 rounded-full bg-[var(--fg)]/15" data-id="element-720" data-mp="2i3ya6jlvnzpq" />
          <span className="h-2.5 w-2.5 rounded-full bg-[var(--fg)]/15" data-id="element-721" data-mp="2i60ymz7dtksc" />
        </div>
        <span className="truncate rounded-full bg-[var(--surface)] px-3 py-1 text-[10.5px] font-medium text-[var(--muted)]" data-id="element-722" data-mp="2e3ebvcpkiq20">
          REKOVELLE® (follitropin delta) · HCP website
        </span>
      </div>

      <div className="relative overflow-hidden" data-id="element-723" data-mp="2n16kjy0sazgf">
        <img src={WEBSITE_SCREENSHOT} alt="Existing REKOVELLE HCP website: 'Optimising fertility treatment with potency and precision' hero, with the indication statement below" className="block w-full" aria-hidden={open || undefined} data-id="element-724" data-mp="1kqo6n1ci9lth" />

        {/* invitation button, placed in the empty space of the indication band */}
        {!open && <button type="button" onClick={() => onOpenChange(true)} aria-label="Open the REKOVELLE teaser Snak" className="group absolute flex cursor-pointer items-center gap-[6%] rounded-[12px] bg-white p-[0.7%] text-left shadow-[0_20px_44px_-18px_rgba(14,27,51,0.5)] ring-2 ring-[#5E4291] transition-transform hover:-translate-y-0.5 hover:scale-[1.03] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--brand)]" style={{
        left: '1.6%',
        top: '68%',
        width: '23%',
        height: '15%'
      }} data-id="element-725" data-mp="23gw699gzxu4x">
            <motion.span aria-hidden="true" className="absolute inset-0 rounded-[12px] ring-[3px] ring-[#5E4291]" animate={{
          opacity: [0.8, 0],
          scale: [1, 1.12]
        }} transition={{
          repeat: Infinity,
          duration: 1.6,
          ease: 'easeOut'
        }} data-id="element-726" data-mp="n3xrwwwsm9dn" />
            <SnakStill screen={0} alt="" className="h-full w-[24%] shrink-0" data-id="element-727" data-mp="5qfwmytrhfze" />
            <span className="min-w-0" data-id="element-728" data-mp="vik3hx9wohr">
              <span className="block text-[clamp(6px,0.7vw,10px)] font-bold uppercase tracking-[0.14em]" style={{
            color: SITE_PURPLE
          }} data-id="element-729" data-mp="yh7pbmbmbmhz">
                New · 40-second case
              </span>
              <span className="mt-[2%] block text-[clamp(8px,1.05vw,14px)] font-extrabold leading-tight text-[var(--fg)]" data-id="element-730" data-mp="ohp30z88jomr">
                Two patients. Same approach?
              </span>
              <span className="mt-[6%] inline-flex items-center gap-1.5 rounded-full px-[0.8vw] py-[0.35vw] text-[clamp(7px,0.85vw,13px)] font-bold text-white shadow-md transition-transform group-hover:scale-105" style={{
            backgroundColor: SITE_PURPLE
          }} data-id="element-731" data-mp="39aagl2sm234y">
                <PlayIcon className="h-[0.9em] w-[0.9em] fill-current" aria-hidden="true" data-id="element-732" data-mp="11qpn6em9dwvi" />
                Play the Snak
              </span>
            </span>
            <motion.span aria-hidden="true" animate={{
          y: [0, -5, 0]
        }} transition={{
          repeat: Infinity,
          duration: 1.4,
          ease: 'easeInOut'
        }} className="absolute -top-11 left-0 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-[var(--fg)] px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-white shadow-lg" data-id="element-733" data-mp="rzuyz4pgcn7f">
              <MousePointerClickIcon className="h-4 w-4 text-[var(--brand-lighter)]" data-id="element-734" data-mp="1aq2xf9i7ipha" />
              Click here to play the Snak
              <span className="absolute -bottom-1.5 left-6 h-3 w-3 rotate-45 bg-[var(--fg)]" data-id="element-735" data-mp="3regl3cke1hzo" />
            </motion.span>
          </button>}

        {/* the live Snak, embedded above the website */}
        <AnimatePresence data-id="element-736" data-mp="3nvr5qe15s6sj">
          {open && <>
              <motion.button key="dim" type="button" aria-label="Close the Snak" onClick={() => onOpenChange(false)} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.35
          }} className="absolute inset-0 cursor-default bg-[rgba(20,12,36,0.55)] focus:outline-none" data-id="element-737" data-mp="3iz0rlw8wzl9r" />
              <motion.div key="snak" role="dialog" aria-label="REKOVELLE teaser Snak embedded above the website" initial={{
            opacity: 0,
            scale: 0.3
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.3
          }} transition={{
            duration: 0.55,
            ease: [0.22, 1, 0.36, 1]
          }} className="absolute flex flex-col overflow-hidden rounded-[14px] bg-white shadow-[0_40px_90px_-30px_rgba(0,0,0,0.6)]" style={{
            left: '50%',
            top: '4%',
            height: '92%',
            aspectRatio: '420 / 820',
            x: '-50%',
            transformOrigin: '-60% 80%'
          }} data-id="element-738" data-mp="3ewnmna1zrre1">
                <div className="flex shrink-0 items-center justify-between px-3 py-2 text-white" style={{
              backgroundColor: SITE_PURPLE
            }} data-id="element-739" data-mp="3fyhdtgw8o0mc">
                  <span className="text-[clamp(8px,0.8vw,11px)] font-bold uppercase tracking-[0.14em]" data-id="element-740" data-mp="2o849j6dxv80f">
                    REKOVELLE · 40-second case
                  </span>
                  <button type="button" onClick={() => onOpenChange(false)} aria-label="Close the Snak" className="flex h-6 w-6 items-center justify-center rounded-full bg-white/15 transition-colors hover:bg-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-741" data-mp="2x66ety4pgmti">
                    <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-742" data-mp="21ynslsgyrapg" />
                  </button>
                </div>
                <ScaledViewport baseWidth={390} baseHeight={760} radius={0} label="Live REKOVELLE teaser Snak" className="min-h-0 flex-1 bg-white" data-id="element-743" data-mp="1x02rzrxsup0h">
                  <iframe src={LIVE_SNAK_URL} title="Live REKOVELLE teaser Snak" width={390} height={760} className="block h-[760px] w-[390px] border-0 bg-white" allow="autoplay; fullscreen" data-id="element-744" data-mp="1j7mu15jr9h6f" />
                </ScaledViewport>
              </motion.div>
            </>}
        </AnimatePresence>
      </div>
    </div>;
}